import React, { useState, useMemo, useEffect } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS, extractPaginationTotal } from "../../config/pagination";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  FormHelperText,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
  Link,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Close,
  CloudUpload,
  CheckCircle,
  Cancel,
  Download,
  OpenInNew,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  useDepartmentAssignmentsOfHod,
  useCreateAssignment,
  useUpdateAssignment,
  useDeleteAssignment,
} from "../../hooks/queries/useAssignments";
import { useClassNames, useSubjectNames } from "../../hooks/queries/useCourses";
import Toast from "../../components/common/Toast";
import ConfirmDialog from "../../components/forms/ConfirmDialog";
import RecordMetadataSection from "../../components/common/RecordMetadataSection";
import SearchBar from "../../components/navigation/SearchBar";
import FormFileUpload from "../../components/forms/FormFileUpload";
import departmentService from "../../services/departmentService";
import courseService from "../../services/courseService";
import assignmentService from "../../services/assignmentService";
import PermissionGate from "../../components/common/PermissionGate";

// Validation Schema
const validationSchema = Yup.object({
  name: Yup.string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters")
    .max(200, "Title must not exceed 200 characters")
    .matches(/^[a-zA-Z0-9\s-]*$/, "Title can only contain letters, numbers, spaces and hyphens"),
  department: Yup.string()
    .required("Department is required"),
  course: Yup.string()
    .required("Course is required"),
  subject: Yup.string()
    .required("Subject is required"),
  class: Yup.string()
    .required("Class is required"),
  dueDate: Yup.date()
    .required("Due date is required")
    .min(
      new Date(new Date().setHours(0, 0, 0, 0)),
      "Due date cannot be in the past"
    )
    .nullable(),
  details: Yup.string()
    .max(1000, "Description must not exceed 1000 characters"),
  evaluationType: Yup.string()
    .oneOf(["marks", "grade"], "Select evaluation type")
    .required("Evaluation type is required"),
  maxMarks: Yup.string().when("evaluationType", {
    is: "marks",
    then: () =>
      Yup.string()
        .required("Max marks is required")
        .matches(/^\d+(\.\d{1,2})?$/, "Max marks must be a valid number")
        .test("positive", "Max marks must be greater than 0", (value) => {
          const num = parseFloat(value);
          return !isNaN(num) && num > 0;
        })
        .test("maxValue", "Max marks must be less than or equal to 1000", (value) => {
          const num = parseFloat(value);
          return !isNaN(num) && num <= 1000;
        }),
    otherwise: () =>
      Yup.string()
        .nullable()
        .test("empty", "Max marks must be empty", (value) => !value),
  }),
  maxGrades: Yup.string().when("evaluationType", {
    is: "grade",
    then: () =>
      Yup.string()
        .transform((value) => (value ? value.toUpperCase() : value))
        .required("Max grade is required")
        .matches(/^[A-Z]$/, "Enter a single letter grade"),
    otherwise: () =>
      Yup.string()
        .nullable()
        .test("empty", "Max grade must be empty", (value) => !value),
  }),
  mixedEvaluation: Yup.mixed().test(
    "exclusive",
    "Provide only max marks or max grade based on evaluation type",
    function () {
      const { evaluationType, maxMarks, maxGrades } = this.parent;
      if (evaluationType === "marks") {
        return !maxGrades;
      }
      if (evaluationType === "grade") {
        return !maxMarks;
      }
      return true;
    }
  ),

  attachments: Yup.array()
    .of(
      Yup.mixed()
        .test("fileSize", "Each file must be less than 100MB", (value) => {
          if (!value) return true; // Optional field
          return value.size <= 100 * 1024 * 1024; // 100MB
        })
        .test("fileType", "Unsupported file format", (value) => {
          if (!value) return true; // Optional field
          const allowedTypes = [
            "application/pdf",
            "image/jpeg",
            "image/png",
            "image/jpg",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "video/mp4",
          ];
          return allowedTypes.includes(value.type);
        })
    )
    .max(5, "Maximum 5 attachments allowed")
    .nullable(),
});

const TeacherAssignments = () => {
  console.log("TeacherAssignments component rendering");

  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");

  // Department, Course, Class, Subject dropdown states
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [departmentLoading, setDepartmentLoading] = useState(false);
  const [courseLoading, setCourseLoading] = useState(false);
  const [classLoading, setClassLoading] = useState(false);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const editSubjectSyncedRef = React.useRef(false);

  // Fetch classes and subjects
  const {
    data: classesResponse,
    isLoading: classesLoading,
    refetch: refetchClasses,
  } = useClassNames();
  const {
    data: subjectsResponse,
    isLoading: subjectsLoading,
    refetch: refetchSubjects,
  } = useSubjectNames();

  // Create, update, and delete assignment mutations
  const createAssignmentMutation = useCreateAssignment();
  const updateAssignmentMutation = useUpdateAssignment();
  const deleteAssignmentMutation = useDeleteAssignment();

  // Extract classes and subjects from API response
  const classes = useMemo(() => {
    if (!classesResponse) return [];
    // Handle different response structures
    let data = classesResponse?.data?.data ||
      classesResponse?.data ||
      classesResponse || [];

    // If it's an object with a classes/items array, extract it
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      data = data.classes || data.items || data.names || [];
    }

    return Array.isArray(data) ? data : [];
  }, [classesResponse]);

  const subjects = useMemo(() => {
    if (!subjectsResponse) return [];
    // Handle different response structures
    let data = subjectsResponse?.data?.data ||
      subjectsResponse?.data ||
      subjectsResponse || [];

    // If it's an object with a subjects/items array, extract it
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      data = data.subjects || data.items || data.names || [];
    }

    return Array.isArray(data) ? data : [];
  }, [subjectsResponse]);

  // Fetch departments
  useEffect(() => {
    let isMounted = true;
    const fetchDepartments = async () => {
      setDepartmentLoading(true);
      try {
        const response = await departmentService.getDepartmentNames();
        const departmentsData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setDepartmentOptions(Array.isArray(departmentsData) ? departmentsData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch departments:", error);
          setDepartmentOptions([]);
        }
      } finally {
        if (isMounted) {
          setDepartmentLoading(false);
        }
      }
    };
    fetchDepartments();
    return () => { isMounted = false; };
  }, []);

  const selectedAssignmentData = selectedAssignment?.originalData;

  const editInitialEvaluationType = useMemo(() => {
    if (!selectedAssignmentData) return "marks";
    const explicitType = selectedAssignmentData.evaluationType;
    if (
      explicitType === "grade" ||
      (!explicitType && (selectedAssignmentData.maxGrades || selectedAssignmentData.maxGrade))
    ) {
      return "grade";
    }
    return "marks";
  }, [selectedAssignmentData]);

  const editInitialMaxMarks = useMemo(() => {
    if (!selectedAssignmentData) return "";
    if (editInitialEvaluationType !== "marks") return "";
    const raw =
      selectedAssignmentData.maxMarks ??
      selectedAssignmentData.maxMark ??
      "";
    return raw === null || raw === undefined ? "" : String(raw);
  }, [selectedAssignmentData, editInitialEvaluationType]);

  const editInitialMaxGrades = useMemo(() => {
    if (!selectedAssignmentData) return "";
    if (editInitialEvaluationType !== "grade") return "";
    const raw =
      selectedAssignmentData.maxGrades ??
      selectedAssignmentData.maxGrade ??
      "";
    return raw ? String(raw).toUpperCase().slice(0, 1) : "";
  }, [selectedAssignmentData, editInitialEvaluationType]);

  // Formik form
  const formik = useFormik({
    initialValues: {
      name: "",
      department: "",
      course: "",
      subject: "",
      class: "",
      dueDate: null,
      details: "",
      maxMarks: "",
      maxGrades: "",
      evaluationType: "marks",
      attachment: null,
      attachments: [],
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // Convert subject and class IDs to names for payload
        let subjectName = values.subject;
        let className = values.class;

        // Only convert if values.subject is an ID (numeric), otherwise use as-is
        if (values.subject && !isNaN(values.subject)) {
          subjectName = subjectOptions.find(s =>
            (s.id ?? s.subjectId ?? s.value) === values.subject
          )?.name ?? values.subject;
        }

        // Only convert if values.class is an ID (numeric), otherwise use as-is  
        if (values.class && !isNaN(values.class)) {
          className = classOptions.find(c =>
            (c.id ?? c.classId ?? c.value) === values.class
          )?.name ?? values.class;
        }

        const assignmentData = {
          name: values.name,
          departmentId: values.department,
          courseId: values.course,
          subjectId: values.subject,
          classId: values.class,
          subject: subjectName,
          class: className,
          details: values.details || "",
          dueDate: values.dueDate ? (() => {
            const date = values.dueDate;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}T18:30:00.000Z`;
          })() : null,
          attachment: values.attachment,
          attachments: values.attachments,
          evaluationType: values.evaluationType,
        };

        if (values.evaluationType === "marks") {
          assignmentData.maxMarks = values.maxMarks;
          assignmentData.maxGrades = null;
        } else {
          assignmentData.maxGrades = values.maxGrades;
          assignmentData.maxMarks = null;
        }

        await createAssignmentMutation.mutateAsync(assignmentData);

        setToastMessage("Assignment created successfully!");
        setToastSeverity("success");

        // Reset form
        resetForm();

        // Close dialog
        setCreateDialogOpen(false);

        // Refetch assignments
        refetch();
      } catch (error) {
        setToastMessage(
          error?.response?.data?.message || error?.message || "Failed to create assignment"
        );
        setToastSeverity("error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Handle file upload
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      const currentAttachments = formik.values.attachments || [];
      const newAttachments = [...currentAttachments, ...files];
      if (newAttachments.length > 5) {
        // Show error if too many files
        formik.setFieldError("attachments", "Maximum 5 attachments allowed");
        return;
      }
      formik.setFieldValue("attachments", newAttachments);
    }
  };

  // Edit validation schema (allows past dates for existing assignments)
  const editValidationSchema = Yup.object({
    name: Yup.string()
      .required("Title is required")
      .min(3, "Title must be at least 3 characters")
      .max(200, "Title must not exceed 200 characters")
      .matches(/^[a-zA-Z0-9\s-]*$/, "Title can only contain letters, numbers, spaces and hyphens"),
    subject: Yup.string()
      .required("Subject is required"),
    class: Yup.string()
      .required("Class is required"),
    dueDate: Yup.date()
      .required("Due date is required")
      .nullable(),
    details: Yup.string()
      .max(1000, "Description must not exceed 1000 characters"),
    evaluationType: Yup.string()
      .oneOf(["marks", "grade"], "Select evaluation type")
      .required("Evaluation type is required"),
    maxMarks: Yup.string().when("evaluationType", {
      is: "marks",
      then: () =>
        Yup.string()
          .required("Max marks is required")
          .matches(/^\d+(\.\d{1,2})?$/, "Max marks must be a valid number")
          .test("positive", "Max marks must be greater than 0", (value) => {
            const num = parseFloat(value);
            return !isNaN(num) && num > 0;
          })
          .test("maxValue", "Max marks must be less than or equal to 1000", (value) => {
            const num = parseFloat(value);
            return !isNaN(num) && num <= 1000;
          }),
      otherwise: () =>
        Yup.string()
          .nullable()
          .test("empty", "Max marks must be empty", (value) => !value),
    }),
    maxGrades: Yup.string().when("evaluationType", {
      is: "grade",
      then: () =>
        Yup.string()
          .transform((value) => (value ? value.toUpperCase() : value))
          .required("Max grade is required")
          .matches(/^[A-Z]$/, "Enter a single letter grade"),
      otherwise: () =>
        Yup.string()
          .nullable()
          .test("empty", "Max grade must be empty", (value) => !value),
    }),
    mixedEvaluation: Yup.mixed().test(
      "exclusive",
      "Provide only max marks or max grade based on evaluation type",
      function () {
        const { evaluationType, maxMarks, maxGrades } = this.parent;
        if (evaluationType === "marks") {
          return !maxGrades;
        }
        if (evaluationType === "grade") {
          return !maxMarks;
        }
        return true;
      }
    ),
    attachment: Yup.mixed()
      .test("fileSize", "File size must be less than 100MB", (value) => {
        if (!value) return true; // Optional field
        return value.size <= 100 * 1024 * 1024; // 100MB
      })
      .test("fileType", "Unsupported file format", (value) => {
        if (!value) return true; // Optional field
        const allowedTypes = [
          "application/pdf",
          "image/jpeg",
          "image/png",
          "image/jpg",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "video/mp4",
        ];
        return allowedTypes.includes(value.type);
      })
      .nullable(),
    attachments: Yup.array()
      .of(
        Yup.mixed()
          .test("fileSize", "Each file must be less than 100MB", (value) => {
            if (!value) return true; // Optional field
            return value.size <= 100 * 1024 * 1024; // 100MB
          })
          .test("fileType", "Unsupported file format", (value) => {
            if (!value) return true; // Optional field
            const allowedTypes = [
              "application/pdf",
              "image/jpeg",
              "image/png",
              "image/jpg",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              "video/mp4",
            ];
            return allowedTypes.includes(value.type);
          })
      )
      .max(5, "Maximum 5 attachments allowed")
      .nullable(),
  });

  // Edit Formik form
  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selectedAssignmentData?.name || "",
      department: selectedAssignmentData?.departmentId || "",
      course: selectedAssignmentData?.courseId || "",
      subject: selectedAssignmentData?.subjectId || "",
      class: selectedAssignmentData?.classId || "",
      dueDate: selectedAssignmentData?.dueDate
        ? (() => {
            const datePart = selectedAssignmentData.dueDate.slice(0, 10); // "YYYY-MM-DD"
            const [year, month, day] = datePart.split("-").map(Number);
            return new Date(year, month - 1, day);
          })()
        : null,
      details: selectedAssignmentData?.details || "",
      maxMarks: editInitialMaxMarks,
      maxGrades: editInitialMaxGrades,
      evaluationType: editInitialEvaluationType,
      attachment: null,
      attachments: selectedAssignmentData?.attachments || [],
    },
    validationSchema: editValidationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // Convert subject and class IDs to names for payload
        let subjectName = values.subject;
        let className = values.class;

        // Only convert if values.subject is an ID (numeric), otherwise use as-is
        if (values.subject && !isNaN(values.subject)) {
          subjectName = subjectOptions.find(s =>
            (s.id ?? s.subjectId ?? s.value) === values.subject
          )?.name ?? values.subject;
        }

        // Only convert if values.class is an ID (numeric), otherwise use as-is  
        if (values.class && !isNaN(values.class)) {
          className = classOptions.find(c =>
            (c.id ?? c.classId ?? c.value) === values.class
          )?.name ?? values.class;
        }

        const assignmentData = {
          name: values.name,
          departmentId: values.department,
          courseId: values.course,
          subjectId: values.subject,
          classId: values.class,
          subject: subjectName,
          class: className,
          details: values.details || "",
          dueDate: values.dueDate ? (() => {
            const date = values.dueDate;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}T18:30:00.000Z`;
          })() : null,
          attachment: values.attachment,
          attachments: values.attachments,
          evaluationType: values.evaluationType,
        };

        if (values.evaluationType === "marks") {
          assignmentData.maxMarks = values.maxMarks;
          assignmentData.maxGrades = null;
        } else {
          assignmentData.maxGrades = values.maxGrades;
          assignmentData.maxMarks = null;
        }

        await updateAssignmentMutation.mutateAsync({
          id: selectedAssignment.id,
          data: assignmentData,
        });

        setToastMessage("Assignment updated successfully!");
        setToastSeverity("success");

        // Refetch dropdown options to get latest data
        await refetchAllOptions();

        // Reset form
        resetForm();
        setSelectedAssignment(null);

        // Close dialog
        setEditDialogOpen(false);

        // Refetch assignments
        refetch();
      } catch (error) {
        setToastMessage(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update assignment"
        );
        setToastSeverity("error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleTitleChange = (e) => {
    const value = e.target.value.slice(0, 200);
    formik.setFieldValue("name", value);
  };
  const handleEditTitleChange = (e) => {
    const value = e.target.value.slice(0, 200);
    editFormik.setFieldValue("name", value);
  };

  // Fetch courses based on department
  useEffect(() => {
    let isMounted = true;
    const fetchCourses = async () => {
      if (!formik.values.department) {
        if (isMounted) {
          setCourseOptions([]);
          setCourseLoading(false);
        }
        return;
      }
      setCourseLoading(true);
      try {
        const params = {
          id: formik.values.department,
          departmentId: formik.values.department,
        };
        const response = await assignmentService.getAssignmentCourseNames(params);
        const coursesDataRaw = response?.data?.data || response?.data || [];
        const coursesArray = Array.isArray(coursesDataRaw)
          ? coursesDataRaw
          : Array.isArray(coursesDataRaw?.courses)
            ? coursesDataRaw.courses
            : [];
        if (!isMounted) return;
        setCourseOptions(coursesArray);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch courses:", error);
          setCourseOptions([]);
        }
      } finally {
        if (isMounted) {
          setCourseLoading(false);
        }
      }
    };
    fetchCourses();
    return () => { isMounted = false; };
  }, [formik.values.department]);

  // Fetch classes based on course
  useEffect(() => {
    let isMounted = true;
    const fetchClasses = async () => {
      if (!formik.values.course) {
        if (isMounted) {
          setClassOptions([]);
          setClassLoading(false);
        }
        return;
      }
      setClassLoading(true);
      try {
        const response = await courseService.getCourseClasses({
          id: formik.values.course,
        });
        const classesData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setClassOptions(Array.isArray(classesData) ? classesData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch classes:", error);
          setClassOptions([]);
        }
      } finally {
        if (isMounted) {
          setClassLoading(false);
        }
      }
    };
    fetchClasses();
    return () => { isMounted = false; };
  }, [formik.values.course]);

  // Fetch subjects based on class
  useEffect(() => {
    let isMounted = true;
    const fetchSubjects = async () => {
      if (!formik.values.class) {
        if (isMounted) {
          setSubjectOptions([]);
          setSubjectLoading(false);
        }
        return;
      }
      setSubjectLoading(true);
      try {
        const params = {
          id: formik.values.class,
        };
        if (formik.values.course) {
          params.courseId = formik.values.course;
        }
        if (formik.values.department) {
          params.departmentId = formik.values.department;
        }
        const response = await assignmentService.getAssignmentSubjectNames(params);
        const subjectsDataRaw = response?.data?.data || response?.data || [];
        const subjectsArray = Array.isArray(subjectsDataRaw)
          ? subjectsDataRaw
          : Array.isArray(subjectsDataRaw?.subjects)
            ? subjectsDataRaw.subjects
            : [];
        if (!isMounted) return;
        setSubjectOptions(subjectsArray);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch subjects:", error);
          setSubjectOptions([]);
        }
      } finally {
        if (isMounted) {
          setSubjectLoading(false);
        }
      }
    };
    fetchSubjects();
    return () => { isMounted = false; };
  }, [formik.values.class, formik.values.course, formik.values.department]);

  // Fetch courses based on department for edit form
  useEffect(() => {
    let isMounted = true;
    const fetchCoursesForEdit = async () => {
      if (!editFormik.values.department) {
        if (isMounted) {
          setCourseOptions([]);
          setCourseLoading(false);
        }
        return;
      }
      setCourseLoading(true);
      try {
        const params = {
          id: editFormik.values.department,
          departmentId: editFormik.values.department,
        };
        const response = await assignmentService.getAssignmentCourseNames(params);
        const coursesDataRaw = response?.data?.data || response?.data || [];
        const coursesArray = Array.isArray(coursesDataRaw)
          ? coursesDataRaw
          : Array.isArray(coursesDataRaw?.courses)
            ? coursesDataRaw.courses
            : [];
        if (!isMounted) return;
        setCourseOptions(coursesArray);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch courses for edit:", error);
          setCourseOptions([]);
        }
      } finally {
        if (isMounted) {
          setCourseLoading(false);
        }
      }
    };
    fetchCoursesForEdit();
    return () => { isMounted = false; };
  }, [editFormik.values.department]);

  // Fetch classes based on course for edit form
  useEffect(() => {
    let isMounted = true;
    const fetchClassesForEdit = async () => {
      if (!editFormik.values.course) {
        if (isMounted) {
          setClassOptions([]);
          setClassLoading(false);
        }
        return;
      }
      setClassLoading(true);
      try {
        const response = await courseService.getCourseClasses({
          id: editFormik.values.course,
        });
        const classesData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setClassOptions(Array.isArray(classesData) ? classesData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch classes for edit:", error);
          setClassOptions([]);
        }
      } finally {
        if (isMounted) {
          setClassLoading(false);
        }
      }
    };
    fetchClassesForEdit();
    return () => { isMounted = false; };
  }, [editFormik.values.course]);

  // Fetch subjects based on class for edit form
  useEffect(() => {
    let isMounted = true;
    const fetchSubjectsForEdit = async () => {
      if (!editFormik.values.class) {
        if (isMounted) {
          setSubjectOptions([]);
          setSubjectLoading(false);
        }
        return;
      }
      setSubjectLoading(true);
      try {
        const response = await assignmentService.getAssignmentSubjectNames({
          id: editFormik.values.class,
        });
        const subjectsData = response?.data?.data || response?.data || [];
        if (!isMounted) return;
        setSubjectOptions(Array.isArray(subjectsData) ? subjectsData : []);
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch subjects for edit:", error);
          setSubjectOptions([]);
        }
      } finally {
        if (isMounted) {
          setSubjectLoading(false);
        }
      }
    };
    fetchSubjectsForEdit();
    return () => { isMounted = false; };
  }, [editFormik.values.class]);

  // Function to refetch all dropdown options
  const refetchAllOptions = async () => {
    try {
      // Refetch departments
      const deptResponse = await departmentService.getDepartmentNames();
      const departmentsData = deptResponse?.data?.data || deptResponse?.data || [];
      setDepartmentOptions(Array.isArray(departmentsData) ? departmentsData : []);

      // Refetch courses if department is selected
      if (editFormik.values.department) {
        const courseParams = {
          id: editFormik.values.department,
          departmentId: editFormik.values.department,
        };
        const courseResponse = await assignmentService.getAssignmentCourseNames(courseParams);
        const coursesDataRaw = courseResponse?.data?.data || courseResponse?.data || [];
        const coursesArray = Array.isArray(coursesDataRaw)
          ? coursesDataRaw
          : Array.isArray(coursesDataRaw?.courses)
            ? coursesDataRaw.courses
            : [];
        setCourseOptions(coursesArray);
      }

      // Refetch classes if course is selected
      if (editFormik.values.course) {
        const classResponse = await courseService.getCourseClasses({
          id: editFormik.values.course,
        });
        const classesData = classResponse?.data?.data || classResponse?.data || [];
        setClassOptions(Array.isArray(classesData) ? classesData : []);
      }

      // Refetch subjects if class is selected
      if (editFormik.values.class) {
        const subjectParams = {
          id: editFormik.values.class,
        };
        if (editFormik.values.course) {
          subjectParams.courseId = editFormik.values.course;
        }
        if (editFormik.values.department) {
          subjectParams.departmentId = editFormik.values.department;
        }
        const subjectResponse = await assignmentService.getAssignmentSubjectNames(subjectParams);
        const subjectsDataRaw = subjectResponse?.data?.data || subjectResponse?.data || [];
        const subjectsArray = Array.isArray(subjectsDataRaw)
          ? subjectsDataRaw
          : Array.isArray(subjectsDataRaw?.subjects)
            ? subjectsDataRaw.subjects
            : [];
        setSubjectOptions(subjectsArray);
      }
    } catch (error) {
      console.error("Failed to refetch options:", error);
    }
  };

  // Handle cascading dropdown changes
  const handleDepartmentChange = (value) => {
    formik.setFieldValue('department', value);
    formik.setFieldValue('course', '');
    formik.setFieldValue('class', '');
    formik.setFieldValue('subject', '');
  };

  const handleCourseChange = (value) => {
    formik.setFieldValue('course', value);
    formik.setFieldValue('class', '');
    formik.setFieldValue('subject', '');
  };

  const handleClassChange = (value) => {
    formik.setFieldValue('class', value);
    formik.setFieldValue('subject', '');
  };

  // Handle cascading dropdown changes for edit form
  const handleEditDepartmentChange = (value) => {
    editFormik.setFieldValue('department', value);
    editFormik.setFieldValue('course', '');
    editFormik.setFieldValue('class', '');
    editFormik.setFieldValue('subject', '');
  };

  const handleEditCourseChange = (value) => {
    editFormik.setFieldValue('course', value);
    editFormik.setFieldValue('class', '');
    editFormik.setFieldValue('subject', '');
  };

  const handleEditClassChange = (value) => {
    editFormik.setFieldValue('class', value);
    editFormik.setFieldValue('subject', '');
  };

  // Handle file upload for edit form
  const handleEditFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      const currentAttachments = editFormik.values.attachments || [];
      const newAttachments = [...currentAttachments, ...files];
      if (newAttachments.length > 5) {
        // Show error if too many files
        editFormik.setFieldError("attachments", "Maximum 5 attachments allowed");
        return;
      }
      editFormik.setFieldValue("attachments", newAttachments);
    }
  };

  // Handle edit button click
  const handleEditClick = (assignment) => {
    setSelectedAssignment(assignment);
    const data = assignment?.originalData || assignment;

    // Fetch all necessary options for the current assignment
    const fetchEditOptions = async () => {
      try {
        // Always fetch departments
        const deptResponse = await departmentService.getDepartmentNames();
        const departmentsData = deptResponse?.data?.data || deptResponse?.data || [];
        setDepartmentOptions(Array.isArray(departmentsData) ? departmentsData : []);

        // Fetch courses if department exists
        if (data.departmentId) {
          const courseParams = {
            id: data.departmentId,
            departmentId: data.departmentId,
          };
          const courseResponse = await assignmentService.getAssignmentCourseNames(courseParams);
          const coursesDataRaw = courseResponse?.data?.data || courseResponse?.data || [];
          const coursesArray = Array.isArray(coursesDataRaw)
            ? coursesDataRaw
            : Array.isArray(coursesDataRaw?.courses)
              ? coursesDataRaw.courses
              : [];
          setCourseOptions(coursesArray);

          // Fetch classes if course exists
          if (data.courseId) {
            const classResponse = await courseService.getCourseClasses({
              id: data.courseId,
            });
            const classesData = classResponse?.data?.data || classResponse?.data || [];
            setClassOptions(Array.isArray(classesData) ? classesData : []);

            // Fetch subjects if class exists
            if (data.classId) {
              const subjectParams = {
                id: data.classId,
              };
              if (data.courseId) {
                subjectParams.courseId = data.courseId;
              }
              if (data.departmentId) {
                subjectParams.departmentId = data.departmentId;
              }
              const subjectResponse = await assignmentService.getAssignmentSubjectNames(subjectParams);
              const subjectsDataRaw = subjectResponse?.data?.data || subjectResponse?.data || [];
              const subjectsArray = Array.isArray(subjectsDataRaw)
                ? subjectsDataRaw
                : Array.isArray(subjectsDataRaw?.subjects)
                  ? subjectsDataRaw.subjects
                  : [];

              // Ensure current subject (by name) is included so edit form shows correct value
              const subjectName = data.subject || assignment.subject;
              const allSubjectOptions = [...subjectsArray];
              if (subjectName && !allSubjectOptions.find(s => (s.name || s.subjectName) === subjectName)) {
                allSubjectOptions.unshift({
                  id: data.subjectId || data.subject,
                  name: subjectName,
                  subjectName: subjectName,
                  value: data.subjectId || data.subject,
                });
              }
              setSubjectOptions(allSubjectOptions);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch options for edit:", error);
      }
    };

    fetchEditOptions();
    setEditDialogOpen(true);
  };

  // Handle delete button click
  const handleDeleteClick = (assignment) => {
    setAssignmentToDelete(assignment);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!assignmentToDelete) return;

    try {
      await deleteAssignmentMutation.mutateAsync(assignmentToDelete.id);

      setToastMessage("Assignment deleted successfully!");
      setToastSeverity("success");

      // Close dialog
      setDeleteDialogOpen(false);
      setAssignmentToDelete(null);

      // Refetch assignments
      refetch();
    } catch (error) {
      setToastMessage(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete assignment"
      );
      setToastSeverity("error");
    }
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    formik.resetForm();
    setCreateDialogOpen(false);
  };

  // Handle edit dialog close
  const handleCloseEditDialog = () => {
    editFormik.resetForm();
    setSelectedAssignment(null);
    setEditDialogOpen(false);
  };

  // Refetch classes and subjects when create dialog opens
  useEffect(() => {
    if (createDialogOpen) {
      refetchClasses();
      refetchSubjects();
    }
  }, [createDialogOpen, refetchClasses, refetchSubjects]);

  // Refetch classes and subjects when edit dialog opens
  useEffect(() => {
    if (editDialogOpen) {
      refetchClasses();
      refetchSubjects();
    }
  }, [editDialogOpen, refetchClasses, refetchSubjects]);

  // Sync edit form subject from assignment subject name when options load (fixes stale subjectId showing wrong option)
  useEffect(() => {
    if (!editDialogOpen || !selectedAssignment || !subjectOptions.length || editSubjectSyncedRef.current) return;
    const data = selectedAssignment?.originalData || selectedAssignment;
    const subjectName = data.subject || selectedAssignment.subject;
    if (!subjectName) return;
    const matchByOption = subjectOptions.find(
      (s) => (s.name || s.subjectName || s.label) === subjectName
    );
    const optionId = matchByOption?.id ?? matchByOption?.subjectId ?? matchByOption?.value;
    const currentId = editFormik.values.subject;
    const currentOption = subjectOptions.find(
      (s) => String(s.id ?? s.subjectId ?? s.value) === String(currentId)
    );
    const currentName = currentOption?.name || currentOption?.subjectName || currentOption?.label;
    if (optionId != null && currentName !== subjectName) {
      editFormik.setFieldValue("subject", optionId);
    }
    editSubjectSyncedRef.current = true;
  }, [editDialogOpen, selectedAssignment, subjectOptions]);

  // Reset edit subject sync ref when edit dialog closes
  useEffect(() => {
    if (!editDialogOpen) editSubjectSyncedRef.current = false;
  }, [editDialogOpen]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPaginationModel((prev) => {
      if (prev.page === 0) {
        return prev;
      }
      return { ...prev, page: 0 };
    });
  }, [debouncedSearchQuery, activeTab]);

  // Prepare query parameters
  const queryParams = useMemo(() => {
    const params = {
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
    };
    if (debouncedSearchQuery) {
      params.search = debouncedSearchQuery;
    }
    return params;
  }, [debouncedSearchQuery, paginationModel]);

  // Fetch assignments from API
  console.log("About to call useDepartmentAssignmentsOfHod hook");
  const {
    data: assignmentsResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useDepartmentAssignmentsOfHod(queryParams);
  console.log("Hook called, result:", { isLoading, isError, hasData: !!assignmentsResponse });

  // Refetch assignments when component mounts (page opens)
  useEffect(() => {
    refetch();
  }, []); // Empty dependency array means this runs once on mount

  // Debug logging
  useEffect(() => {
    console.log("TeacherAssignments - Query State:", {
      isLoading,
      isError,
      error,
      hasData: !!assignmentsResponse,
      assignmentsResponse,
    });
  }, [isLoading, isError, error, assignmentsResponse]);

  // Extract and transform assignments data
  const assignments = useMemo(() => {
    if (!assignmentsResponse) {
      console.log("No assignmentsResponse");
      return [];
    }

    console.log("assignmentsResponse:", assignmentsResponse);

    // Extract assignments array from response structure: { data: { data: { assignments: [...] } } }
    const assignmentsArray = assignmentsResponse?.data?.data?.assignments ||
      assignmentsResponse?.data?.assignments ||
      (Array.isArray(assignmentsResponse?.data) ? assignmentsResponse.data : []) ||
      (Array.isArray(assignmentsResponse) ? assignmentsResponse : []);

    console.log("Extracted assignmentsArray:", assignmentsArray);

    if (!Array.isArray(assignmentsArray) || assignmentsArray.length === 0) {
      console.log("No assignments found in response");
      return [];
    }

    // Transform to match DataGrid format
    const transformed = assignmentsArray.map((assignment) => {
      // Format due date
      let formattedDueDate = "";
      if (assignment.dueDate) {
        const date = new Date(assignment.dueDate);
        formattedDueDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      }

      // Determine status based on due date
      let status = "active";
      if (assignment.dueDate) {
        const dueDate = new Date(assignment.dueDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dueDate < today) {
          status = "closed";
        }
      }

      const submissionCount =
        assignment.submissionCount ??
        (assignment.submissions != null && (assignment.total != null || assignment.totalStudents != null)
          ? `${assignment.submissions}/${assignment.total ?? assignment.totalStudents}`
          : "0/0");

      return {
        id: assignment.id,
        title: assignment.name || assignment.title || "",
        subject: assignment.subject || "",
        class: assignment.classDisplayName || assignment.class || "",
        dueDate: formattedDueDate,
        status: assignment.status || status,
        submissionCount,
        students: assignment.students ?? [],
        // Keep original data for reference
        originalData: assignment,
      };
    });

    console.log("Transformed assignments:", transformed);
    return transformed;
  }, [assignmentsResponse]);

  const totalAssignments = useMemo(() => {
    const fromApi = extractPaginationTotal(assignmentsResponse, assignments.length);
    return fromApi;
  }, [assignmentsResponse, assignments.length]);

  // Filter assignments based on active tab
  const filteredAssignments = useMemo(() => {
    let list = assignments;
    if (activeTab === 1) list = assignments.filter((a) => a.status === "active");
    if (activeTab === 2) list = assignments.filter((a) => a.status === "closed" || a.status === "completed");

    if (subjectFilter !== "all") {
      list = list.filter((a) => a.subject === subjectFilter);
    }
    if (classFilter !== "all") {
      list = list.filter((a) => a.class === classFilter);
    }

    return list;
  }, [assignments, activeTab, subjectFilter, classFilter]);

  const submissions = [
    {
      id: 1,
      student: "John Doe",
      rollNo: "STU001",
      submittedAt: "2025-12-18",
      status: "pending",
      grade: null,
    },
    {
      id: 2,
      student: "Jane Smith",
      rollNo: "STU002",
      submittedAt: "2025-12-17",
      status: "graded",
      grade: 18,
    },
    {
      id: 3,
      student: "Mike Johnson",
      rollNo: "STU003",
      submittedAt: "2025-12-19",
      status: "pending",
      grade: null,
    },
  ];

  const columns = [
    { field: "title", headerName: "Assignment", flex: 1, minWidth: 200 },
    { field: "subject", headerName: "Subject", width: 300, minWidth: 260 },
    { field: "class", headerName: "Class", width: 120 },
    { field: "dueDate", headerName: "Due Date", width: 120 },
    {
      field: "submissionCount",
      headerName: "Submissions",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ?? "0/0"}
          size="small"
          color="primary"
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "active" ? "success" : "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedAssignment(params.row);
              setViewDialogOpen(true);
            }}
          >
            <Visibility />
          </IconButton>
          <PermissionGate permission="assignment.update">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              title="Edit Assignment"
            >
              <Edit />
            </IconButton>
          </PermissionGate>
          <PermissionGate permission="assignment.delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
              title="Delete Assignment"
            >
              <Delete />
            </IconButton>
          </PermissionGate>
        </Box>
      ),
    },
  ];

  const submissionColumns = [
    {
      field: "student",
      headerName: "Student",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ width: 28, height: 28 }}>
            {params.row.student[0]}
          </Avatar>
          <Box>
            <Typography variant="body2">{params.row.student}</Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.rollNo}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "submittedAt", headerName: "Submitted", width: 120 },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "graded" ? "success" : "warning"}
        />
      ),
    },
    {
      field: "grade",
      headerName: "Grade",
      width: 80,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: () => (
        <Box>
          <IconButton size="small" color="success">
            <CheckCircle />
          </IconButton>
          <IconButton size="small" color="error">
            <Cancel />
          </IconButton>
          <IconButton size="small">
            <Visibility />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage assignments
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* <Button
            variant="outlined"
            onClick={() => {
              console.log("Manual refetch triggered");
              refetch();
            }}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "Refresh"}
          </Button> */}
          <PermissionGate permission="assignment.create">
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Assignment
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ width: 350 }}>
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search assignments..."
                onClear={() => setSearchQuery("")}
              />
            </Box>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={subjectFilter}
                label="Subject"
                onChange={(e) => setSubjectFilter(e.target.value)}
              >
                <MenuItem value="all">All Subjects</MenuItem>
                {subjects.map((s) => (
                  <MenuItem key={s.id || s.name} value={s.name}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Class</InputLabel>
              <Select
                value={classFilter}
                label="Class"
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <MenuItem value="all">All Classes</MenuItem>
                {classes.map((c) => {
                  const displayValue = [c.name ?? c.className ?? c.label, c.section].filter(Boolean).join(" - ");
                  return (
                    <MenuItem key={c.id || c.name} value={displayValue}>
                      {displayValue}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            {(subjectFilter !== "all" || classFilter !== "all") && (
              <Button 
                size="small" 
                onClick={() => {
                  setSubjectFilter("all");
                  setClassFilter("all");
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{ mb: 2 }}
          >
            <Tab label="All Assignments" />
            <Tab label="Active" />
            <Tab label="Closed" />
          </Tabs>

          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Error loading assignments
              </Typography>
              <Typography variant="caption" component="div">
                {error?.response?.data?.message || error?.message || "Failed to load assignments. Please try again."}
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 1, fontFamily: "monospace" }}>
                {error?.response?.config?.url || error?.config?.url || "Unknown endpoint"}
              </Typography>
            </Alert>
          )}

          {isLoading && filteredAssignments.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={filteredAssignments}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              paginationModel={paginationModel}
              onPaginationModelChange={(newModel) => {
                setPaginationModel((prev) => {
                  if (prev.pageSize !== newModel.pageSize) {
                    return { page: 0, pageSize: newModel.pageSize };
                  }
                  return newModel;
                });
              }}
              rowCount={totalAssignments}
              paginationMode="server"
              loading={isLoading}
              sx={{ border: "none" }}
            />
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Create Assignment
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box component="form" onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  required
                  name="name"
                  value={formik.values.name}
                  onChange={handleTitleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder="Letters, numbers and spaces, 3–200 characters"
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={formik.touched.department && Boolean(formik.errors.department)}
                >
                  <InputLabel>Department</InputLabel>
                  <Select
                    name="department"
                    value={formik.values.department}
                    label="Department"
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    onBlur={formik.handleBlur}
                    disabled={departmentLoading}
                  >
                    {departmentLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      departmentOptions.map((dept) => (
                        <MenuItem key={dept.id ?? dept.departmentId ?? dept.name} value={dept.id ?? dept.departmentId ?? dept.value}>
                          {dept.name ?? dept.departmentName ?? dept.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {formik.touched.department && formik.errors.department && (
                    <FormHelperText>{formik.errors.department}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={formik.touched.course && Boolean(formik.errors.course)}
                >
                  <InputLabel>Course</InputLabel>
                  <Select
                    name="course"
                    value={formik.values.course}
                    label="Course"
                    onChange={(e) => handleCourseChange(e.target.value)}
                    onBlur={formik.handleBlur}
                    disabled={courseLoading || !formik.values.department}
                  >
                    {courseLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      courseOptions.map((course) => (
                        <MenuItem key={course.id ?? course.courseId ?? course.name} value={course.id ?? course.courseId ?? course.value}>
                          {course.name ?? course.courseName ?? course.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {formik.touched.course && formik.errors.course && (
                    <FormHelperText>{formik.errors.course}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={formik.touched.class && Boolean(formik.errors.class)}
                >
                  <InputLabel>Class</InputLabel>
                  <Select
                    name="class"
                    value={formik.values.class}
                    label="Class"
                    onChange={(e) => handleClassChange(e.target.value)}
                    onBlur={formik.handleBlur}
                    disabled={classLoading || !formik.values.course}
                  >
                    {classLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      classOptions.map((cls) => (
                        <MenuItem key={cls.id ?? cls.classId ?? cls.name} value={cls.id ?? cls.classId ?? cls.value}>
                          {[cls.name ?? cls.className ?? cls.label, cls.section].filter(Boolean).join(" - ")}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {formik.touched.class && formik.errors.class && (
                    <FormHelperText>{formik.errors.class}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={formik.touched.subject && Boolean(formik.errors.subject)}
                >
                  <InputLabel>Subject</InputLabel>
                  <Select
                    name="subject"
                    value={formik.values.subject}
                    label="Subject"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={subjectLoading || !formik.values.class}
                  >
                    {subjectLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      subjectOptions.map((subject) => (
                        <MenuItem key={subject.id ?? subject.subjectId ?? subject.name} value={subject.id ?? subject.subjectId ?? subject.value}>
                          {subject.name ?? subject.subjectName ?? subject.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {formik.touched.subject && formik.errors.subject && (
                    <FormHelperText>{formik.errors.subject}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="Due Date *"
                  required
                  value={formik.values.dueDate}
                  onChange={(date) => formik.setFieldValue("dueDate", date)}
                  minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(formik.errors.dueDate),
                      helperText: formik.errors.dueDate || (formik.touched.dueDate && formik.errors.dueDate),
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" error={Boolean(formik.errors.evaluationType)}>
                  <FormLabel component="legend">Evaluation Type</FormLabel>
                  <RadioGroup
                    row
                    name="evaluationType"
                    value={formik.values.evaluationType}
                    onChange={(event) => {
                      const value = event.target.value;
                      formik.setFieldValue("evaluationType", value);
                      if (value === "marks") {
                        formik.setFieldValue("maxGrades", "");
                      } else {
                        formik.setFieldValue("maxMarks", "");
                      }
                    }}
                  >
                    <FormControlLabel value="marks" control={<Radio />} label="Marks" />
                    <FormControlLabel value="grade" control={<Radio />} label="Grade" />
                  </RadioGroup>
                  {formik.touched.evaluationType && formik.errors.evaluationType && (
                    <FormHelperText>{formik.errors.evaluationType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {formik.values.evaluationType === "marks" ? (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Marks *"
                    type="text"
                    name="maxMarks"
                    value={formik.values.maxMarks}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.maxMarks && Boolean(formik.errors.maxMarks)}
                    helperText={formik.touched.maxMarks && formik.errors.maxMarks}
                    inputProps={{ maxLength: 6 }}
                    placeholder="Enter a number up to 1000"
                  />
                </Grid>
              ) : (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Grade"
                    name="maxGrades"
                    value={formik.values.maxGrades}
                    onChange={(event) => {
                      const value = event.target.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, "")
                        .slice(0, 1);
                      formik.setFieldValue("maxGrades", value);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.maxGrades && Boolean(formik.errors.maxGrades)}
                    helperText={formik.touched.maxGrades && formik.errors.maxGrades}
                    placeholder="Single letter grade"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  name="details"
                  value={formik.values.details}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.details && Boolean(formik.errors.details)}
                  helperText={
                    formik.touched.details && formik.errors.details
                      ? formik.errors.details
                      : `${formik.values.details.length}/1000 characters`
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormFileUpload
                  name="attachments"
                  value={formik.values.attachments}
                  onChange={(files) => {
                    formik.setFieldValue("attachments", files);
                    formik.setFieldTouched("attachments", true);
                  }}
                  multiple={true}
                  label="Upload Attachments (Optional)"
                  maxSize={100}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4"
                />
                {formik.touched.attachments && formik.errors.attachments && (
                  <FormHelperText error sx={{ mt: 0.5, ml: 1.75 }}>
                    {formik.errors.attachments}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={formik.isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={
                formik.isSubmitting ||
                createAssignmentMutation.isPending ||
                !formik.values.name ||
                !formik.values.subject ||
                !formik.values.class ||
                !formik.values.dueDate ||
                Object.keys(formik.errors).length > 0
              }
            >
              {formik.isSubmitting || createAssignmentMutation.isPending
                ? "Creating..."
                : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseEditDialog();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Edit Assignment
            <IconButton onClick={handleCloseEditDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box component="form" onSubmit={editFormik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  required
                  name="name"
                  value={editFormik.values.name}
                  onChange={handleEditTitleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.name && Boolean(editFormik.errors.name)}
                  helperText={editFormik.touched.name && editFormik.errors.name}
                  placeholder="Letters, numbers and spaces, 3–200 characters"
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={editFormik.touched.department && Boolean(editFormik.errors.department)}
                >
                  <InputLabel>Department</InputLabel>
                  <Select
                    name="department"
                    value={editFormik.values.department}
                    label="Department"
                    onChange={(e) => handleEditDepartmentChange(e.target.value)}
                    onBlur={editFormik.handleBlur}
                    disabled={departmentLoading}
                  >
                    {departmentLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      departmentOptions.map((dept) => (
                        <MenuItem key={dept.id ?? dept.departmentId ?? dept.name} value={dept.id ?? dept.departmentId ?? dept.value}>
                          {dept.name ?? dept.departmentName ?? dept.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {editFormik.touched.department && editFormik.errors.department && (
                    <FormHelperText>{editFormik.errors.department}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={editFormik.touched.course && Boolean(editFormik.errors.course)}
                >
                  <InputLabel>Course</InputLabel>
                  <Select
                    name="course"
                    value={editFormik.values.course}
                    label="Course"
                    onChange={(e) => handleEditCourseChange(e.target.value)}
                    onBlur={editFormik.handleBlur}
                    disabled={courseLoading || !editFormik.values.department}
                  >
                    {courseLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      courseOptions.map((course) => (
                        <MenuItem key={course.id ?? course.courseId ?? course.name} value={course.id ?? course.courseId ?? course.value}>
                          {course.name ?? course.courseName ?? course.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {editFormik.touched.course && editFormik.errors.course && (
                    <FormHelperText>{editFormik.errors.course}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={editFormik.touched.class && Boolean(editFormik.errors.class)}
                >
                  <InputLabel>Class</InputLabel>
                  <Select
                    name="class"
                    value={editFormik.values.class}
                    label="Class"
                    onChange={(e) => handleEditClassChange(e.target.value)}
                    onBlur={editFormik.handleBlur}
                    disabled={classLoading || !editFormik.values.course}
                  >
                    {classLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      classOptions.map((cls) => (
                        <MenuItem key={cls.id ?? cls.classId ?? cls.name} value={cls.id ?? cls.classId ?? cls.value}>
                          {[cls.name ?? cls.className ?? cls.label, cls.section].filter(Boolean).join(" - ")}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {editFormik.touched.class && editFormik.errors.class && (
                    <FormHelperText>{editFormik.errors.class}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  fullWidth
                  required
                  error={editFormik.touched.subject && Boolean(editFormik.errors.subject)}
                >
                  <InputLabel>Subject</InputLabel>
                  <Select
                    name="subject"
                    value={editFormik.values.subject}
                    label="Subject"
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    disabled={subjectLoading || !editFormik.values.class}
                  >
                    {subjectLoading ? (
                      <MenuItem value="">
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      subjectOptions.map((subject) => (
                        <MenuItem key={subject.id ?? subject.subjectId ?? subject.name} value={subject.id ?? subject.subjectId ?? subject.value}>
                          {subject.name ?? subject.subjectName ?? subject.label}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {editFormik.touched.subject && editFormik.errors.subject && (
                    <FormHelperText>{editFormik.errors.subject}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="Due Date *"
                  required
                  value={editFormik.values.dueDate}
                  onChange={(date) => editFormik.setFieldValue("dueDate", date)}
                  minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: editFormik.touched.dueDate && Boolean(editFormik.errors.dueDate),
                      helperText: editFormik.touched.dueDate && editFormik.errors.dueDate,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl component="fieldset" error={Boolean(editFormik.errors.evaluationType)}>
                  <FormLabel component="legend">Evaluation Type</FormLabel>
                  <RadioGroup
                    row
                    name="evaluationType"
                    value={editFormik.values.evaluationType}
                    onChange={(event) => {
                      const value = event.target.value;
                      editFormik.setFieldValue("evaluationType", value);
                      if (value === "marks") {
                        editFormik.setFieldValue("maxGrades", "");
                        editFormik.setFieldTouched("maxGrades", true, false);
                      } else {
                        editFormik.setFieldValue("maxMarks", "");
                        editFormik.setFieldTouched("maxMarks", true, false);
                      }
                    }}
                  >
                    <FormControlLabel value="marks" control={<Radio />} label="Marks" />
                    <FormControlLabel value="grade" control={<Radio />} label="Grade" />
                  </RadioGroup>
                  {editFormik.touched.evaluationType && editFormik.errors.evaluationType && (
                    <FormHelperText>{editFormik.errors.evaluationType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {editFormik.values.evaluationType === "marks" ? (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Marks *"
                    type="text"
                    name="maxMarks"
                    value={editFormik.values.maxMarks}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    error={editFormik.touched.maxMarks && Boolean(editFormik.errors.maxMarks)}
                    helperText={editFormik.touched.maxMarks && editFormik.errors.maxMarks}
                    inputProps={{ maxLength: 6 }}
                    placeholder="Enter a number up to 100"
                  />
                </Grid>
              ) : (
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max Grade"
                    name="maxGrades"
                    value={editFormik.values.maxGrades}
                    onChange={(event) => {
                      const value = event.target.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, "")
                        .slice(0, 1);
                      editFormik.setFieldValue("maxGrades", value);
                    }}
                    onBlur={editFormik.handleBlur}
                    error={editFormik.touched.maxGrades && Boolean(editFormik.errors.maxGrades)}
                    helperText={editFormik.touched.maxGrades && editFormik.errors.maxGrades}
                    placeholder="Single letter grade"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  name="details"
                  value={editFormik.values.details}
                  onChange={editFormik.handleChange}
                  onBlur={editFormik.handleBlur}
                  error={editFormik.touched.details && Boolean(editFormik.errors.details)}
                  helperText={
                    editFormik.touched.details && editFormik.errors.details
                      ? editFormik.errors.details
                      : `${editFormik.values.details.length}/1000 characters`
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormFileUpload
                  name="edit-attachments"
                  value={editFormik.values.attachments}
                  onChange={(files) => {
                    editFormik.setFieldValue("attachments", files);
                    editFormik.setFieldTouched("attachments", true);
                  }}
                  multiple={true}
                  label="Upload Attachments (Optional)"
                  maxSize={100}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.mp4"
                />
                {editFormik.touched.attachments && editFormik.errors.attachments && (
                  <FormHelperText error sx={{ mt: 0.5, ml: 1.75 }}>
                    {editFormik.errors.attachments}
                  </FormHelperText>
                )}
                {selectedAssignment?.originalData?.attachments && selectedAssignment.originalData.attachments.length > 0 && editFormik.values.attachments.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75, display: "block" }}>
                    Current: {selectedAssignment.originalData.attachments.length} file(s)
                  </Typography>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog} disabled={editFormik.isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={editFormik.isSubmitting || updateAssignmentMutation.isPending || !editFormik.isValid}
            >
              {editFormik.isSubmitting || updateAssignmentMutation.isPending
                ? "Updating..."
                : "Update"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Assignment"
        message={
          <>
            Are you sure you want to delete the assignment{" "}
            <strong>"{assignmentToDelete?.title}"</strong>? This action cannot be undone.
          </>
        }
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        loading={deleteAssignmentMutation.isPending}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="error"
      />

      {/* Toast Notification */}
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />

      {/* View Assignment Details Dialog - deferred close so nothing flashes after close */}
      {viewDialogOpen && selectedAssignment && (
        <Dialog
          open={true}
          onClose={(e, reason) => {
            if (reason === "backdropClick" || reason === "escapeKeyDown") return;
            e?.stopPropagation?.();
            e?.preventDefault?.();
            requestAnimationFrame(() => {
              setViewDialogOpen(false);
              setSelectedAssignment(null);
            });
          }}
          TransitionProps={{ timeout: 0 }}
          disableRestoreFocus
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">Assignment Details</Typography>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  requestAnimationFrame(() => {
                    setViewDialogOpen(false);
                    setSelectedAssignment(null);
                  });
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedAssignment?.originalData && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Title
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedAssignment.originalData.name}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Subject
                    </Typography>
                    <Chip label={selectedAssignment.originalData.subject} size="small" color="primary" />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Class
                    </Typography>
                    <Chip label={selectedAssignment.originalData.classSection?`${selectedAssignment.originalData.class} - ${selectedAssignment.originalData.classSection}`:selectedAssignment.originalData.class} size="small" />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Due Date
                    </Typography>
                    <Typography variant="body1">
                      {selectedAssignment.originalData.dueDate
                        ? new Date(selectedAssignment.originalData.dueDate).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                        : "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Evaluation Type
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ textTransform: "capitalize" }}>
                      {selectedAssignment.originalData.evaluationType ||
                        (selectedAssignment.originalData.maxGrades || selectedAssignment.originalData.maxGrade
                          ? "grade"
                          : "marks")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {selectedAssignment.originalData.evaluationType === "grade" ||
                        selectedAssignment.originalData.maxGrades ||
                        selectedAssignment.originalData.maxGrade
                        ? "Max Grade"
                        : "Max Marks"}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedAssignment.originalData.evaluationType === "grade" ||
                        selectedAssignment.originalData.maxGrades ||
                        selectedAssignment.originalData.maxGrade
                        ? selectedAssignment.originalData.maxGrades ||
                        selectedAssignment.originalData.maxGrade ||
                        "N/A"
                        : selectedAssignment.originalData.maxMarks || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                      {selectedAssignment.originalData.details || "No description provided"}
                    </Typography>
                  </Grid>

                  {/* Teacher - commented out
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Teacher
                  </Typography>
                  <Typography variant="body1">
                    {selectedAssignment.originalData.teacherName || "N/A"}
                  </Typography>
                </Grid>
                */}

                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Chip
                      label={selectedAssignment.status}
                      size="small"
                      color={selectedAssignment.status === "active" ? "success" : "default"}
                    />
                  </Grid>

                  {selectedAssignment.originalData.attachments && selectedAssignment.originalData.attachments.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Attachments ({selectedAssignment.originalData.attachments.length})
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {selectedAssignment.originalData.attachments.map((attachment, index) => (
                          <Link
                            key={index}
                            href={attachment.url || attachment}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: "primary.main",
                              textDecoration: "none",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            <OpenInNew fontSize="small" />
                            <Typography variant="body1">
                              {attachment.name || attachment.originalName || `Attachment ${index + 1}`}
                            </Typography>
                          </Link>
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {/* Legacy single attachment support */}
                  {selectedAssignment.originalData.attachment && !selectedAssignment.originalData.attachments?.length && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Attachment
                      </Typography>
                      <Link
                        href={selectedAssignment.originalData.attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          color: "primary.main",
                          textDecoration: "none",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        <OpenInNew fontSize="small" />
                        <Typography variant="body1">
                          View Attachment
                        </Typography>
                      </Link>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Submissions
                    </Typography>
                    <Chip
                      label={selectedAssignment.submissionCount ?? "0/0"}
                      size="small"
                      color="primary"
                    />
                    {/* Student list - optional: uncomment to show students and submission status
                  {selectedAssignment.students?.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Student list
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {selectedAssignment.students.map((s) => (
                          <Typography key={s.studentId} component="li" variant="body2" sx={{ py: 0.25 }}>
                            Student {s.studentId}: {s.isSubmitted ? "Submitted" : "Pending"}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                  */}
                  </Grid>

                  <Grid item xs={12}>
                    <RecordMetadataSection record={selectedAssignment.originalData} />
                  </Grid>
                </Grid>
              </Box>
            )
            }
          </DialogContent>
          <DialogActions>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                requestAnimationFrame(() => {
                  setViewDialogOpen(false);
                  setSelectedAssignment(null);
                });
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default TeacherAssignments;
